/**
 * ActionDetailModal — reusable Action detail drawer (Feb 2026 · sprint 375).
 *
 * ONE canonical component used by:
 *   • MeetingNotesPanel (Actions tab item click)
 *   • PreviousActionsPanel (carry-forward action click)
 *   • ActionsLibraryTab (library row click)
 *
 * Uses the tenant-guarded /review-actions endpoints so it works even when
 * the originating review has been deleted.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { X, AlertTriangle, Clock, Send, Paperclip, Download, FileSpreadsheet, FileText, Trash2 } from 'lucide-react';

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', done: 'Done', dropped: 'Dropped' };
const PRIORITY_LABELS = { low: 'Low', normal: 'Normal', high: 'High' };
const ATTACH_MAX_MB = 25;
const ATTACH_EXTS = ['xlsx', 'xlsm', 'pdf'];

function _overdueOf(a) {
  if (!a || !a.due_date) return false;
  if (a.status === 'done' || a.status === 'dropped') return false;
  return a.due_date < new Date().toISOString().slice(0, 10);
}

export function ActionDetailModal({ actionId, onClose, onUpdated }) {
  const { API, getHeaders, token } = useAuth();
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ text: '', status: '' });
  const [meta, setMeta] = useState({ owner_text: '', due_date: '', priority: 'normal', status: 'open' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [outletName, setOutletName] = useState('');   // sprint 376 · lookup for display
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, attRes] = await Promise.all([
        fetch(`${API}/review-actions/${actionId}`, { headers: getHeaders() }),
        fetch(`${API}/review-actions/${actionId}/attachments`, { headers: getHeaders() }),
      ]);
      if (!aRes.ok) throw new Error();
      const a = await aRes.json();
      setAction(a);
      setMeta({
        owner_text: a.owner_text || '',
        due_date: a.due_date || '',
        priority: a.priority || 'normal',
        status: a.status || 'open',
      });
      if (attRes.ok) {
        const att = await attRes.json();
        setAttachments(att.items || []);
      }
      // Best-effort outlet name lookup
      if (a.outlet_id) {
        try {
          const oR = await fetch(`${API}/outlets`, { headers: getHeaders() });
          if (oR.ok) {
            const list = await oR.json();
            const o = (list || []).find(x => x.id === a.outlet_id);
            if (o) setOutletName(o.name);
          }
        } catch { /* silent */ }
      }
    } catch { toast.error('Failed to load action'); }
    finally { setLoading(false); }
  }, [API, getHeaders, actionId]);
  useEffect(() => { load(); }, [load]);

  const submitUpdate = async () => {
    if (!draft.text.trim()) return toast.error('Enter update text');
    try {
      const r = await fetch(`${API}/review-actions/${actionId}/updates`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: draft.text.trim(), status: draft.status || null }),
      });
      if (!r.ok) throw new Error();
      setDraft({ text: '', status: '' });
      await load(); onUpdated?.();
      toast.success('Update added');
    } catch { toast.error('Failed'); }
  };

  const saveMeta = async () => {
    if (!action) return;
    setSavingMeta(true);
    try {
      // Metadata patch — reuse the review-scoped route (tenant guarded via review load)
      const r = await fetch(`${API}/reviews/${action.originating_review_id}/actions/${actionId}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(meta),
      });
      if (!r.ok) throw new Error();
      await load(); onUpdated?.();
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
    finally { setSavingMeta(false); }
  };

  // sprint 376 · Attach evidence file
  const onFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // reset so same file can be picked again
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ATTACH_EXTS.includes(ext)) {
      return toast.error(`Only ${ATTACH_EXTS.join(', ')} allowed`);
    }
    if (f.size > ATTACH_MAX_MB * 1024 * 1024) {
      return toast.error(`File exceeds ${ATTACH_MAX_MB} MB limit`);
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await fetch(`${API}/review-actions/${actionId}/attachments`, {
        method: 'POST',
        headers: getHeaders(),  // no explicit Content-Type — browser sets boundary
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      await load(); onUpdated?.();
      toast.success('Evidence attached');
    } catch (err) { toast.error(err.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await fetch(
        `${API}/review-actions/${actionId}/attachments/${att.id}/source`,
        { headers: getHeaders() });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = att.filename;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"?`)) return;
    try {
      const r = await fetch(
        `${API}/review-actions/${actionId}/attachments/${att.id}`,
        { method: 'DELETE', headers: getHeaders() });
      if (!r.ok) throw new Error();
      await load(); onUpdated?.();
      toast.success('Removed');
    } catch { toast.error('Delete failed'); }
  };

  if (!action && !loading) return null;
  const overdue = _overdueOf(action);

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="action-detail-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-3 border-b border-[#E7EBE6] flex items-start justify-between shrink-0">
          <div className="min-w-0 flex-1 pr-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-widest text-[#7A8B82]">Action</span>
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                action?.status === 'done' ? 'bg-emerald-100 text-emerald-700' :
                action?.status === 'dropped' ? 'bg-gray-100 text-gray-600' :
                action?.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'}`} data-testid="ad-status-badge">
                {STATUS_LABELS[action?.status] || '—'}
              </span>
              {overdue && (
                <span className="text-[10px] uppercase tracking-wider text-red-600 inline-flex items-center gap-0.5" data-testid="ad-overdue">
                  <AlertTriangle className="w-3 h-3"/>Overdue
                </span>
              )}
            </div>
            <h2 className="text-lg font-semibold text-[color:var(--color-text-primary)] leading-tight" data-testid="ad-task">
              {action?.task}
            </h2>
            <p className="text-[11px] text-[#5C6B64] mt-1" data-testid="ad-origin">
              From <span className="text-[color:var(--color-primary-hover)]">{action?.originating_review_title || '—'}</span>
              {action?.review_series_id && <> · series-linked</>}
            </p>
            {action?.context_label && (
              <p className="text-[11px] text-[color:var(--color-primary-hover)] mt-0.5" data-testid="ad-context">
                On: {action.context_label}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="ad-close" className="p-1 text-[#7A8B82] hover:text-red-600 shrink-0">
            <X className="w-5 h-5"/>
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {/* Metadata editor */}
          <div className="px-5 py-4 border-b border-[#E7EBE6] bg-[#FDFDFC]">
            <p className="text-[10px] uppercase tracking-wider text-[#7A8B82] mb-2">Details</p>
            {/* sprint 376 · Outlet row (read-only display; assignment stays server-authoritative) */}
            <div className="mb-3" data-testid="ad-outlet-row">
              <Label className="text-[11px] text-[#5C6B64]">Outlet</Label>
              <div className="h-8 flex items-center px-2 mt-1 text-xs text-[#1A2421] bg-white border border-[#E7EBE6] rounded"
                    data-testid="ad-outlet-value">
                {outletName || action?.outlet_id || '—'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-[#5C6B64]">Owner</Label>
                <Input value={meta.owner_text}
                       onChange={(e) => setMeta(m => ({ ...m, owner_text: e.target.value }))}
                       data-testid="ad-owner" className="h-8 text-xs mt-1"/>
              </div>
              <div>
                <Label className="text-[11px] text-[#5C6B64]">Due Date</Label>
                <Input type="date" value={meta.due_date}
                       onChange={(e) => setMeta(m => ({ ...m, due_date: e.target.value }))}
                       data-testid="ad-due" className="h-8 text-xs mt-1"/>
              </div>
              <div>
                <Label className="text-[11px] text-[#5C6B64]">Priority</Label>
                <select value={meta.priority}
                        onChange={(e) => setMeta(m => ({ ...m, priority: e.target.value }))}
                        data-testid="ad-priority"
                        className="h-8 w-full text-xs border border-[#E7EBE6] rounded px-2 mt-1">
                  {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-[#5C6B64]">Status</Label>
                <select value={meta.status}
                        onChange={(e) => setMeta(m => ({ ...m, status: e.target.value }))}
                        data-testid="ad-metastatus"
                        className="h-8 w-full text-xs border border-[#E7EBE6] rounded px-2 mt-1">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end">
              <Button size="sm" onClick={saveMeta} disabled={savingMeta}
                      data-testid="ad-save-meta"
                      className="h-8 text-xs bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white">
                {savingMeta ? 'Saving…' : 'Save Details'}
              </Button>
            </div>
          </div>

          {/* sprint 376 · Evidence / Attachments */}
          <div className="px-5 py-4 border-b border-[#E7EBE6]" data-testid="ad-evidence-section">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Paperclip className="w-3.5 h-3.5 text-[#7A8B82]"/>
                <p className="text-[10px] uppercase tracking-wider text-[#7A8B82] font-semibold">
                  Evidence / Attachments
                </p>
                <span className="text-[10px] text-[#B4C0BB]">({attachments.length})</span>
              </div>
              <input ref={fileInputRef} type="file" onChange={onFilePicked}
                     accept=".xlsx,.xlsm,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                     className="hidden"
                     data-testid="ad-evidence-input"/>
              <Button size="sm" variant="outline" disabled={uploading}
                       onClick={() => fileInputRef.current?.click()}
                       data-testid="ad-evidence-attach"
                       className="h-8 text-xs gap-1">
                <Paperclip className="w-3.5 h-3.5"/>
                {uploading ? 'Uploading…' : 'Attach Evidence'}
              </Button>
            </div>
            <p className="text-[10px] text-[#B4C0BB] mb-2">
              Allowed: .xlsx, .pdf · up to {ATTACH_MAX_MB} MB · uploaded evidence is immutable
            </p>
            <div className="space-y-1.5">
              {attachments.map(att => {
                const Icon = att.extension === 'pdf' ? FileText : FileSpreadsheet;
                return (
                  <div key={att.id}
                        className="flex items-center gap-2 px-2 py-1.5 border border-[#EEF1EC] rounded bg-white"
                        data-testid={`ad-evidence-item-${att.id}`}>
                    <Icon className="w-4 h-4 text-[color:var(--color-primary-hover)] shrink-0"/>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-[#1A2421] truncate" title={att.filename}>{att.filename}</p>
                      <p className="text-[10px] text-[#B4C0BB]">
                        {att.uploaded_by_email} · {new Date(att.uploaded_at).toLocaleString()}
                        · {(att.size_bytes / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button onClick={() => downloadAttachment(att)}
                             data-testid={`ad-evidence-download-${att.id}`}
                             className="p-1 text-[#7A8B82] hover:text-[color:var(--color-primary-hover)]"
                             aria-label="Download">
                      <Download className="w-3.5 h-3.5"/>
                    </button>
                    <button onClick={() => deleteAttachment(att)}
                             data-testid={`ad-evidence-delete-${att.id}`}
                             className="p-1 text-[#7A8B82] hover:text-red-600"
                             aria-label="Delete">
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                );
              })}
              {!attachments.length && (
                <p className="text-xs text-[#B4C0BB] italic" data-testid="ad-evidence-empty">
                  No evidence attached yet.
                </p>
              )}
            </div>
          </div>

          {/* Timeline */}
          <div className="px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-3.5 h-3.5 text-[#7A8B82]"/>
              <p className="text-[10px] uppercase tracking-wider text-[#7A8B82] font-semibold">Progress Timeline</p>
              <span className="text-[10px] text-[#B4C0BB]">({(action?.update_history || []).length})</span>
            </div>
            <div className="space-y-3">
              {(action?.update_history || []).map(u => (
                <div key={u.id} className="pl-3 border-l-2 border-[color:var(--color-primary)]/40"
                     data-testid={`ad-history-${u.id}`}>
                  <p className="text-sm text-[#1A2421]">{u.text}</p>
                  <p className="text-[10px] text-[#B4C0BB] mt-0.5">
                    {u.author_email} · {new Date(u.created_at).toLocaleString()}
                    {u.status && <> · status → <span className="text-[color:var(--color-primary-hover)]">{STATUS_LABELS[u.status] || u.status}</span></>}
                    {u.from_review_id && u.from_review_id !== action?.originating_review_id && (
                      <span className="ml-1 text-[color:var(--color-primary-hover)]">· carried across reviews</span>
                    )}
                  </p>
                </div>
              ))}
              {!action?.update_history?.length && (
                <p className="text-xs text-[#B4C0BB] italic">No progress updates yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Sticky composer */}
        <div className="border-t border-[#E7EBE6] bg-[#FDFDFC] px-5 py-3 shrink-0">
          <Label className="text-[10px] uppercase tracking-wider text-[#7A8B82] font-semibold">Add Progress Update</Label>
          <textarea value={draft.text}
                    onChange={(e) => setDraft(d => ({ ...d, text: e.target.value }))}
                    rows={2}
                    placeholder="What changed? What's next?"
                    data-testid="ad-update-input"
                    className="w-full mt-1 text-sm border border-[#E7EBE6] rounded p-2 resize-none outline-none focus:border-[color:var(--color-primary)]"/>
          <div className="flex items-center gap-2 mt-2">
            <select value={draft.status}
                    onChange={(e) => setDraft(d => ({ ...d, status: e.target.value }))}
                    data-testid="ad-update-status"
                    className="h-8 text-xs border border-[#E7EBE6] rounded px-2">
              <option value="">(keep current status)</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>Change to {v}</option>)}
            </select>
            <Button size="sm" onClick={submitUpdate} data-testid="ad-update-submit"
                    className="h-8 text-xs bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white gap-1 ml-auto">
              <Send className="w-3.5 h-3.5"/> Save Update
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
